import { RIDE_DETAILS_API_BASE } from "@/config/apiConfig";
import { publicAuthorizedFetch } from "@/services/publicAuthorizedFetch";
import { getSystemTimezoneAbbreviation } from "@/utils/timezone";
import { formatApiDate, formatApiTime } from "@/utils/dateTimeFormat";
import { isRideCompletedAfterEnd } from "@/utils/rideCompletion";
import { resolveApiErrorMessage } from "@/utils/apiErrorMessage";
import type {
  RideApiSeat,
  RideApiStop,
  RideDetails,
  RideDetailsApiResponse,
  RideDetailsContext,
  RideDetailsLocation,
} from "@/types/rideDetails";

const RIDE_INVITATION_MESSAGE =
  "I've already planned this trip and would be happy to share the ride with you. Enjoy a safe, affordable, and eco-friendly journey together. Seats are limited—book yours now!";

export interface FetchRideDetailsParams {
  rideId: string;
  /** Defaults to "host". */
  context?: RideDetailsContext;
}

/**
 * Fetches ride details for the given ride ID and maps the raw API payload into the UI
 * model. Only API-provided fields are used — no static placeholder content.
 *
 * - `rideId` and the system timezone abbreviation (e.g. "IST") are sent as query params.
 * - `context` ("host") and `rideId` are sent in the request body.
 * - If `rideOffer.rid` is missing, the API `message` is shown as an error.
 */
export async function fetchRideDetails({
  rideId,
  context = "host",
}: FetchRideDetailsParams): Promise<RideDetails> {
  const trimmedRideId = rideId.trim();
  if (!trimmedRideId) {
    throw new Error("Ride ID is required.");
  }

  const query = new URLSearchParams({
    rideId: trimmedRideId,
    timezone: getSystemTimezoneAbbreviation(),
  });

  const res = await publicAuthorizedFetch(
    `${RIDE_DETAILS_API_BASE}/ride-details?${query.toString()}`,
    {
      method: "POST",
      headers: {
        accept: "*/*",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ context, rideId: trimmedRideId }),
    }
  );

  const data = (await res.json().catch(() => ({}))) as RideDetailsApiResponse;

  if (res.status === 404) {
    throw new Error(
      resolveApiErrorMessage(
        data,
        "Ride not found. Please check the Ride ID and try again."
      )
    );
  }

  if (!res.ok) {
    throw new Error(
      resolveApiErrorMessage(
        data,
        `Unable to fetch ride details (status ${res.status}).`
      )
    );
  }

  if (data.rideOffer?.rid == null) {
    throw new Error(
      resolveApiErrorMessage(
        data,
        "Ride not found. Please check the Ride ID and try again."
      )
    );
  }

  return mapRideDetails(data, trimmedRideId);
}

function mapRideDetails(
  api: RideDetailsApiResponse,
  rideId: string
): RideDetails {
  const startStop = api.route?.sp;
  const endStop = api.route?.ep;
  const seating = api.seating ?? [];

  const vehicleName =
    [api.vehicle?.mk, api.vehicle?.mdl].filter(Boolean).join(" ") || "Vehicle";

  const { pricePerSeat, currency } = resolveSeatPrice(seating);

  const hostName = api.rideOffer?.ridername?.trim() || "Host";

  const displayRideId =
    api.rideNumber ??
    api.rideOffer?.rideNumber ??
    rideId;

  return {
    rideId: displayRideId,
    raw: api,
    host: {
      name: hostName,
      rating: api.hostRating,
      isVerified: api.isVerifiedHost,
      bioTitle: api.bioTitle,
      bio: api.bioDescription,
      invitationMessage: RIDE_INVITATION_MESSAGE,
    },
    date: formatApiDate(startStop?.dt),
    time: formatApiTime(startStop?.tm),
    timezone: startStop?.tz,
    vehicle: {
      name: vehicleName,
      make: api.vehicle?.mk,
      model: api.vehicle?.mdl,
      imageUrl: undefined,
      registration: api.vehicle?.reg,
      color: api.vehicle?.clr,
      features: api.vehicle?.prefs?.length ? api.vehicle.prefs : undefined,
    },
    route: {
      pickup: mapStop(startStop, "Pickup") ?? emptyLocation("Pickup"),
      drop: mapStop(endStop, "Drop") ?? emptyLocation("Drop"),
    },
    pricing: {
      pricePerSeat,
      currency,
    },
    seats: {
      total: countBookableSeats(seating),
      available: countAvailableSeats(seating),
    },
    isRideCompleted: isRideCompletedAfterEnd(endStop),
  };
}

function emptyLocation(label: string): RideDetailsLocation {
  return { label, address: "—" };
}

function mapStop(
  stop: RideApiStop | undefined,
  defaultLabel: string
): RideDetailsLocation | null {
  if (!stop) return null;
  return {
    label: defaultLabel,
    locality: stop.sc?.trim() || undefined,
    address: stop.sl || stop.sc || "—",
    lat: stop.lat,
    lng: stop.lng,
  };
}

function countAvailableSeats(seating: RideApiSeat[]): number {
  return seating.filter((seat) => seat.ss === "AVAILABLE").length;
}

function countBookableSeats(seating: RideApiSeat[]): number {
  return seating.filter((seat) => seat.ss !== "NOTAVAILABLE").length;
}

function resolveSeatPrice(
  seating: RideApiSeat[]
): { pricePerSeat: number; currency?: string } {
  const reference =
    seating.find((seat) => seat.ss === "AVAILABLE" && seat.spr != null) ??
    seating.find((seat) => seat.spr != null);

  return {
    pricePerSeat: reference?.spr ?? 0,
    currency: reference?.cry,
  };
}
